// frontend/src/components/Events/EventParticipantsModal.js
import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  Button,
  IconButton,
  Avatar,
  Chip,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Divider,
  Tab,
  Tabs,
  Badge,
  TextField,
  InputAdornment,
  CircularProgress,
  Skeleton,
  Tooltip,
  Drawer,
  Paper,
  Grid,
  Slide,
  alpha,
  useTheme
} from '@mui/material';
import {
  Close as CloseIcon,
  Cancel as CancelIcon,
  Person as PersonIcon,
  Search as SearchIcon,
  HowToReg as HowToRegIcon,
  LocationOn as LocationOnIcon,
  Description as DescriptionIcon,
  Check as CheckIcon,
  Close as CloseIconSmall,
  Verified as VerifiedIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { participantsAPI } from '../../services/eventsAPI';

// 🔥 СТИЛИЗОВАННЫЕ КОМПОНЕНТЫ — iOS / Glass
const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-container': { padding: theme.spacing(2) },
  '& .MuiDialog-paper': {
    width: 'min(760px, calc(100vw - 32px))',
    maxWidth: 760,
    minWidth: 0,
    height: 'min(82vh, 760px)',
    maxHeight: 'calc(100vh - 32px)',
    margin: 0,
    overflow: 'hidden',
    borderRadius: theme.spacing(4.5),
    border: `1px solid ${alpha(theme.palette.common.white, 0.84)}`,
    background: `linear-gradient(145deg, ${alpha(theme.palette.common.white, 0.78)}, ${alpha(theme.palette.grey[100], 0.50)} 58%, ${alpha(theme.palette.primary.main, 0.055)}), radial-gradient(circle at 92% 8%, ${alpha(theme.palette.primary.main, 0.11)}, transparent 34%)`,
    backgroundColor: alpha(theme.palette.common.white, 0.62),
    backdropFilter: 'blur(30px) saturate(150%)',
    WebkitBackdropFilter: 'blur(30px) saturate(150%)',
    boxShadow: `0 28px 90px ${alpha(theme.palette.common.black, 0.22)}`,
  },
  '& .MuiBackdrop-root': {
    backgroundColor: alpha(theme.palette.common.black, 0.32),
  },
}));

const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
  padding: theme.spacing(2.2, 2.6),
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  flexShrink: 0,
  background: `linear-gradient(135deg, ${alpha(theme.palette.common.white, 0.60)}, ${alpha(theme.palette.common.white, 0.18)})`,
  borderBottom: `1px solid ${alpha(theme.palette.common.white, 0.72)}`,
}));

const SearchField = styled(TextField)(({ theme }) => ({
  marginTop: theme.spacing(1.5),
  '& .MuiOutlinedInput-root': {
    borderRadius: 999,
    minHeight: 40,
    height: 40,
    backgroundColor: alpha(theme.palette.common.white, 0.48),
    backdropFilter: 'blur(14px)',
    WebkitBackdropFilter: 'blur(14px)',
    transition: 'all 0.2s ease',
    '& fieldset': {
      borderColor: alpha(theme.palette.common.white, 0.78),
    },
    '&:hover fieldset': {
      borderColor: alpha(theme.palette.primary.main, 0.30),
    },
    '&.Mui-focused': {
      backgroundColor: alpha(theme.palette.common.white, 0.68),
      '& fieldset': {
        borderWidth: 1,
        borderColor: alpha(theme.palette.primary.main, 0.52),
      }
    }
  }
}));

const StyledTabs = styled(Tabs)(({ theme }) => ({
  minHeight: 46,
  padding: theme.spacing(0.55),
  borderRadius: 999,
  backgroundColor: alpha(theme.palette.common.white, 0.36),
  border: `1px solid ${alpha(theme.palette.common.white, 0.72)}`,
  '& .MuiTabs-flexContainer': { gap: theme.spacing(0.5) },
  '& .MuiTab-root': {
    minHeight: 38,
    minWidth: 0,
    flex: 1,
    padding: theme.spacing(0.6, 1.1),
    borderRadius: 999,
    textTransform: 'none',
    fontWeight: 650,
    fontSize: '0.82rem',
    color: theme.palette.text.secondary,
    transition: 'all 0.18s ease',
    '&.Mui-selected': {
      color: theme.palette.primary.main,
      backgroundColor: alpha(theme.palette.primary.main, 0.11),
      boxShadow: `0 5px 16px ${alpha(theme.palette.primary.main, 0.08)}`,
    },
  },
  '& .MuiTabs-indicator': { display: 'none' },
}));

const ParticipantListItem = styled(ListItem)(({ theme }) => ({
  borderRadius: theme.spacing(2.8),
  marginBottom: theme.spacing(1),
  padding: theme.spacing(1.25, 1.35),
  minHeight: 72,
  background: `linear-gradient(145deg, ${alpha(theme.palette.common.white, 0.54)}, ${alpha(theme.palette.common.white, 0.20)})`,
  border: `1px solid ${alpha(theme.palette.common.white, 0.76)}`,
  boxShadow: `0 7px 22px ${alpha(theme.palette.common.black, 0.045)}`,
  transition: 'all 0.18s ease',
  cursor: 'pointer',
  '&:hover': {
    transform: 'translateY(-1px)',
    borderColor: alpha(theme.palette.primary.main, 0.34),
    boxShadow: `0 11px 28px ${alpha(theme.palette.primary.main, 0.09)}`,
  }
}));

const ActionButton = styled(Button)(({ theme, actioncolor }) => ({
  minWidth: 36,
  width: 36,
  height: 36,
  borderRadius: 999,
  padding: 0,
  backgroundColor: actioncolor === 'approve'
    ? alpha(theme.palette.success.main, 0.10)
    : alpha(theme.palette.error.main, 0.09),
  color: actioncolor === 'approve'
    ? theme.palette.success.main
    : theme.palette.error.main,
  border: `1px solid ${alpha(actioncolor === 'approve' ? theme.palette.success.main : theme.palette.error.main, 0.12)}`,
  transition: 'all 0.18s ease',
  '&:hover': {
    backgroundColor: actioncolor === 'approve'
      ? theme.palette.success.main
      : theme.palette.error.main,
    color: theme.palette.common.white,
    transform: 'scale(1.04)',
  }
}));

const StatusChip = styled(Chip)(({ theme, statuscolor }) => ({
  borderRadius: 999,
  height: 22,
  fontWeight: 650,
  fontSize: '0.66rem',
  backgroundColor: statuscolor === 'approved'
    ? alpha(theme.palette.success.main, 0.10)
    : statuscolor === 'pending'
    ? alpha(theme.palette.warning.main, 0.11)
    : alpha(theme.palette.error.main, 0.10),
  color: statuscolor === 'approved'
    ? theme.palette.success.main
    : statuscolor === 'pending'
    ? theme.palette.warning.main
    : theme.palette.error.main,
  border: `1px solid ${alpha(statuscolor === 'approved' ? theme.palette.success.main : statuscolor === 'pending' ? theme.palette.warning.main : theme.palette.error.main, 0.12)}`,
  '& .MuiChip-icon': { color: 'inherit' }
}));

const SectionChip = styled(Chip)(({ theme, bgcolor }) => ({
  borderRadius: 999,
  height: 28,
  backgroundColor: bgcolor || alpha(theme.palette.common.white, 0.44),
  color: theme.palette.text.primary,
  fontWeight: 600,
  fontSize: '0.72rem',
  maxWidth: 180,
  border: `1px solid ${alpha(theme.palette.common.white, 0.72)}`,
  '& .MuiChip-label': {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    padding: '0 11px'
  }
}));

const StyledDrawer = styled(Drawer)(({ theme }) => ({
  '& .MuiDrawer-paper': {
    width: 390,
    maxWidth: 'calc(100vw - 24px)',
    borderTopLeftRadius: theme.spacing(4),
    borderBottomLeftRadius: theme.spacing(4),
    padding: theme.spacing(2.2),
    boxSizing: 'border-box',
    zIndex: 1300,
    position: 'fixed',
    right: 0,
    top: 16,
    height: 'calc(100vh - 32px)',
    border: `1px solid ${alpha(theme.palette.common.white, 0.82)}`,
    background: `linear-gradient(145deg, ${alpha(theme.palette.common.white, 0.78)}, ${alpha(theme.palette.grey[100], 0.48)}), radial-gradient(circle at 100% 0%, ${alpha(theme.palette.primary.main, 0.10)}, transparent 38%)`,
    backdropFilter: 'blur(30px) saturate(150%)',
    WebkitBackdropFilter: 'blur(30px) saturate(150%)',
    boxShadow: `-20px 20px 70px ${alpha(theme.palette.common.black, 0.20)}`,
  }
}));

const DetailSection = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(1.8),
  marginBottom: theme.spacing(1.5),
  borderRadius: theme.spacing(2.8),
  background: `linear-gradient(145deg, ${alpha(theme.palette.common.white, 0.52)}, ${alpha(theme.palette.common.white, 0.20)})`,
  border: `1px solid ${alpha(theme.palette.common.white, 0.76)}`,
  boxShadow: `0 7px 22px ${alpha(theme.palette.common.black, 0.04)}`,
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontSize: '0.68rem',
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '0.6px',
  color: theme.palette.text.secondary,
  marginBottom: theme.spacing(1)
}));

const ListContainer = styled(Box)(({ theme }) => ({
  flex: 1,
  minHeight: 0,
  overflow: 'auto',
  paddingRight: theme.spacing(0.5),
  '&::-webkit-scrollbar': { width: '6px' },
  '&::-webkit-scrollbar-track': { background: 'transparent' },
  '&::-webkit-scrollbar-thumb': {
    background: alpha(theme.palette.text.secondary, 0.16),
    borderRadius: 999,
    '&:hover': { background: alpha(theme.palette.primary.main, 0.32) },
  }
}));

const SummaryPill = styled(Box)(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: theme.spacing(0.65),
  minHeight: 28,
  padding: theme.spacing(0.45, 1.05),
  borderRadius: 999,
  backgroundColor: alpha(theme.palette.common.white, 0.42),
  border: `1px solid ${alpha(theme.palette.common.white, 0.72)}`,
  color: theme.palette.text.secondary,
  fontSize: '0.72rem',
  fontWeight: 650,
}));

// 🔥 Вспомогательная функция для инициалов
const getInitials = (firstName, lastName) => {
  if (!firstName && !lastName) return '?';
  return `${(firstName?.[0] || '')}${(lastName?.[0] || '')}`.toUpperCase();
};

// 🔥 КОМПОНЕНТ ДЛЯ АНИМИРОВАННОГО УДАЛЕНИЯ
const AnimatedParticipantItem = ({
  participant,
  showActions,
  onApprove,
  onReject,
  onItemClick
}) => {
  const theme = useTheme();
  const [exiting, setExiting] = useState(false);
  const [exitDirection, setExitDirection] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  const handleApprove = async (e) => {
    e.stopPropagation();
    setExitDirection('right');
    setExiting(true);
    setActionLoading(true);

    try {
      await onApprove(participant, e);
    } catch (error) {
      console.error('Ошибка при одобрении:', error);
      setExiting(false);
      setExitDirection(null);
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async (e) => {
    e.stopPropagation();
    setExitDirection('left');
    setExiting(true);
    setActionLoading(true);

    try {
      await onReject(participant, e);
    } catch (error) {
      console.error('Ошибка при отклонении:', error);
      setExiting(false);
      setExitDirection(null);
    } finally {
      setActionLoading(false);
    }
  };

  if (exiting) {
    return (
      <Box
        sx={{
          height: 80,
          mb: 1,
          opacity: 0,
          transform: `translateX(${exitDirection === 'right' ? '24px' : '-24px'})`,
          transition: 'opacity 220ms ease, transform 220ms ease',
        }}
      />
    );
  }

  return (
    <Slide
      direction="up"
      in={!exiting}
      mountOnEnter
      unmountOnExit
      timeout={220}
    >
      <ParticipantListItem
        onClick={() => onItemClick(participant)}
      >
        <ListItemAvatar sx={{ minWidth: 64 }}>
          <Badge
            overlap="circular"
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            badgeContent={
              participant.participation_type === 'speaker' ? (
                <Tooltip title="Докладчик">
                  <Box
                    sx={{
                      bgcolor: 'primary.main',
                      borderRadius: theme.spacing(1),
                      width: 20,
                      height: 20,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: '2px solid white',
                      boxShadow: theme.shadows[1]
                    }}
                  >
                    <Typography variant="caption" sx={{ color: 'white', fontSize: '0.6rem' }}>
                      🎤
                    </Typography>
                  </Box>
                </Tooltip>
              ) : null
            }
          >
            <Avatar
              src={participant.user?.avatar}
              sx={{
                width: 46,
                height: 46,
                bgcolor: participant.participation_type === 'speaker'
                  ? 'primary.main'
                  : 'grey.400',
                boxShadow: theme.shadows[1]
              }}
            >
              {getInitials(participant.user?.first_name, participant.user?.last_name)}
            </Avatar>
          </Badge>
        </ListItemAvatar>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
            <Typography variant="subtitle1" fontWeight="600" noWrap>
              {participant.user?.first_name} {participant.user?.last_name}
            </Typography>
            {participant.status === 'pending' && (
              <StatusChip
                label="Ожидает"
                size="small"
                statuscolor="pending"
                icon={<HowToRegIcon style={{ fontSize: 14 }} />}
              />
            )}
            {participant.status === 'approved' && (
              <StatusChip
                label="Подтверждён"
                size="small"
                statuscolor="approved"
                icon={<VerifiedIcon style={{ fontSize: 14 }} />}
              />
            )}
            {participant.status === 'rejected' && (
              <StatusChip
                label="Отклонён"
                size="small"
                statuscolor="rejected"
                icon={<CancelIcon style={{ fontSize: 14 }} />}
              />
            )}
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
            {participant.user?.email}
          </Typography>

          {participant.user?.branch && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <LocationOnIcon sx={{ fontSize: 12, color: 'text.disabled' }} />
              <Typography variant="caption" color="text.disabled" noWrap>
                {participant.user.branch}
              </Typography>
            </Box>
          )}
        </Box>

        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          ml: 'auto',
          mr: 2,
          minWidth: showActions ? 116 : 150
        }}>
          <Box sx={{
            minWidth: showActions ? 0 : 120,
            maxWidth: 180,
            display: 'flex',
            justifyContent: 'flex-end'

          }}>
            {participant.section ? (
              <Tooltip title={participant.section.description || participant.section.title}>
                <SectionChip
                  label={participant.section.title}
                  bgcolor={participant.section.color ? alpha(participant.section.color, 0.2) : undefined}
                />
              </Tooltip>
            ) : participant.participation_type === 'speaker' ? (
              <SectionChip
                label="Пленарное заседание"
                bgcolor={alpha(theme.palette.primary.main, 0.1)}
              />
            ) : null}
          </Box>

          {showActions && (
            <Box sx={{ display: 'flex', gap: 1 }}>
              <ActionButton
                actioncolor="approve"
                onClick={handleApprove}
                disabled={actionLoading}
              >
                {actionLoading ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  <CheckIcon />
                )}
              </ActionButton>
              <ActionButton
                actioncolor="reject"
                onClick={handleReject}
                disabled={actionLoading}
              >
                {actionLoading ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  <CloseIconSmall />
                )}
              </ActionButton>
            </Box>
          )}
        </Box>
      </ParticipantListItem>
    </Slide>
  );
};


// ============================================
// SKELETON ЗАГРУЗКИ УЧАСТНИКОВ
// Визуально повторяет подход EventProgramModal_skeleton:
// fade-in 180ms + glass shimmer 1.6s.
// ============================================
const ParticipantsSkeleton = ({ theme }) => (
  <Box
    sx={{
      animation: 'participantsSkeletonFadeIn 180ms ease-out',
      '@keyframes participantsSkeletonFadeIn': {
        from: { opacity: 0 },
        to: { opacity: 1 },
      },
      '@keyframes participantsSkeletonShimmer': {
        '0%': { backgroundPosition: '-420px 0' },
        '100%': { backgroundPosition: '420px 0' },
      },
    }}
  >
    {[0, 1, 2, 3].map((item) => (
      <Box
        key={item}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          mb: 1,
          p: 1.35,
          minHeight: 80,
          borderRadius: 3,
          background: `linear-gradient(145deg,
            ${alpha(theme.palette.common.white, 0.40)},
            ${alpha(theme.palette.common.white, 0.18)}
          )`,
          border: `1px solid ${alpha(theme.palette.common.white, 0.30)}`,
        }}
      >
        <Skeleton
          variant="circular"
          width={52}
          height={52}
          sx={{
            flexShrink: 0,
            background: `linear-gradient(90deg,
              ${alpha(theme.palette.common.white, 0.30)} 25%,
              ${alpha(theme.palette.common.white, 0.62)} 50%,
              ${alpha(theme.palette.common.white, 0.30)} 75%
            )`,
            backgroundSize: '840px 100%',
            animation: 'participantsSkeletonShimmer 1.6s ease-in-out infinite',
            animationDelay: `${item * 80}ms`,
          }}
        />

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Skeleton
            variant="text"
            width={item % 2 === 0 ? '48%' : '58%'}
            height={25}
            sx={{
              borderRadius: 2,
              background: `linear-gradient(90deg,
                ${alpha(theme.palette.common.white, 0.30)} 25%,
                ${alpha(theme.palette.common.white, 0.62)} 50%,
                ${alpha(theme.palette.common.white, 0.30)} 75%
              )`,
              backgroundSize: '840px 100%',
              animation: 'participantsSkeletonShimmer 1.6s ease-in-out infinite',
            }}
          />
          <Skeleton
            variant="text"
            width={item % 2 === 0 ? '72%' : '62%'}
            height={20}
            sx={{
              borderRadius: 2,
              background: `linear-gradient(90deg,
                ${alpha(theme.palette.common.white, 0.24)} 25%,
                ${alpha(theme.palette.common.white, 0.52)} 50%,
                ${alpha(theme.palette.common.white, 0.24)} 75%
              )`,
              backgroundSize: '840px 100%',
              animation: 'participantsSkeletonShimmer 1.6s ease-in-out infinite',
              animationDelay: '80ms',
            }}
          />
        </Box>

        <Skeleton
          variant="rounded"
          width={72}
          height={28}
          sx={{
            flexShrink: 0,
            borderRadius: 999,
            display: { xs: 'none', sm: 'block' },
            background: `linear-gradient(90deg,
              ${alpha(theme.palette.common.white, 0.30)} 25%,
              ${alpha(theme.palette.common.white, 0.62)} 50%,
              ${alpha(theme.palette.common.white, 0.30)} 75%
            )`,
            backgroundSize: '840px 100%',
            animation: 'participantsSkeletonShimmer 1.6s ease-in-out infinite',
            animationDelay: '120ms',
          }}
        />
      </Box>
    ))}
  </Box>
);

const EventParticipantsModal = ({
  open,
  onClose,
  eventId,
  participants,
  canManageApplications,  // 🔥 заменяем isOrganizer на canManageApplications
  onParticipantsUpdate
}) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const [tabValue, setTabValue] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [detailDrawerOpen, setDetailDrawerOpen] = useState(false);
  const [drawerParticipant, setDrawerParticipant] = useState(null);
  const [actionLoading, setActionLoading] = useState({});
  const [isRefreshing, setIsRefreshing] = useState(false);

  const pendingRequests = useMemo(
    () => participants.filter(p => p.status === 'pending'),
    [participants]
  );
  const approvedParticipants = useMemo(
    () => participants.filter(p => p.status === 'approved'),
    [participants]
  );
  const rejectedParticipants = useMemo(
    () => participants.filter(p => p.status === 'rejected'),
    [participants]
  );

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleRowClick = (participant) => {
    setDrawerParticipant(participant);
    setDetailDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setDetailDrawerOpen(false);
    setDrawerParticipant(null);
  };

  // Переход на профиль выбранного участника
  const handleOpenParticipantProfile = () => {
    const userId = drawerParticipant?.user?.id;

    if (!userId) {
      console.warn('⚠️ Не удалось открыть профиль: у участника отсутствует user.id');
      return;
    }

    handleCloseDrawer();
    navigate(`/profile/${userId}`);
  };

  // 🔄 Свежие данные всегда получаем через родителя.
  // EventDetailPage остаётся единственным источником истины для participants.
  const refreshDataFromServer = async (showSkeleton = false) => {
    if (!onParticipantsUpdate) {
      console.error('❌ EventParticipantsModal: onParticipantsUpdate не передан.');
      return null;
    }

    if (showSkeleton) {
      setIsRefreshing(true);
    }

    try {
      const result = await onParticipantsUpdate();

      // Если открыт drawer участника — синхронизируем и его.
      if (result && drawerParticipant) {
        const updatedParticipant = result.find(p => p.id === drawerParticipant.id);

        if (updatedParticipant) {
          setDrawerParticipant({ ...updatedParticipant });
        } else {
          handleCloseDrawer();
        }
      }

      return result || null;
    } catch (err) {
      console.error('❌ EventParticipantsModal: ошибка при обновлении данных:', err);
      return null;
    } finally {
      if (showSkeleton) {
        setIsRefreshing(false);
      }
    }
  };

  // При каждом новом открытии модалки получаем свежий список.
  // Это не polling: запрос выполняется только в момент открытия.
  useEffect(() => {
    if (open && eventId) {
      refreshDataFromServer(true);
    }
  }, [open, eventId]);

  const handleApprove = async (participant, event) => {
    event.stopPropagation();
    console.log('✅ Одобряем заявку:', participant.id);
    setActionLoading(prev => ({ ...prev, [participant.id]: 'approve' }));

    try {
      await participantsAPI.approveParticipant(participant.id);
      console.log('✅ API запрос выполнен успешно');
      await refreshDataFromServer(false);
    } catch (err) {
      console.error('❌ Ошибка при одобрении:', err);
    } finally {
      setActionLoading(prev => {
        const newState = { ...prev };
        delete newState[participant.id];
        return newState;
      });
    }
  };

  const handleReject = async (participant, event) => {
    event.stopPropagation();
    console.log('❌ Отклоняем заявку:', participant.id);
    setActionLoading(prev => ({ ...prev, [participant.id]: 'reject' }));

    try {
      await participantsAPI.rejectParticipant(participant.id);
      console.log('✅ API запрос выполнен успешно');
      await refreshDataFromServer(false);
    } catch (err) {
      console.error('❌ Ошибка при отклонении:', err);
    } finally {
      setActionLoading(prev => {
        const newState = { ...prev };
        delete newState[participant.id];
        return newState;
      });
    }
  };

  const filterParticipants = (list) => {
    if (!searchQuery) return list;

    const query = searchQuery.toLowerCase();
    return list.filter(p =>
      p.user?.first_name?.toLowerCase().includes(query) ||
      p.user?.last_name?.toLowerCase().includes(query) ||
      p.user?.email?.toLowerCase().includes(query) ||
      (p.user?.branch && p.user.branch.toLowerCase().includes(query)) ||
      (p.section?.title && p.section.title.toLowerCase().includes(query))
    );
  };

  const filteredPending = useMemo(
    () => filterParticipants(pendingRequests),
    [pendingRequests, searchQuery]
  );
  const filteredApproved = useMemo(
    () => filterParticipants(approvedParticipants),
    [approvedParticipants, searchQuery]
  );
  const filteredRejected = useMemo(
    () => filterParticipants(rejectedParticipants),
    [rejectedParticipants, searchQuery]
  );

  const renderParticipantDetails = () => {
    if (!drawerParticipant) return null;

    const sectionColor = drawerParticipant.section?.color || theme.palette.primary.main;
    const sectionBgColor = alpha(sectionColor, 0.1);

    return (
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h5" fontWeight="700" color="primary.main">
            Детали заявки
          </Typography>
          <IconButton onClick={handleCloseDrawer} sx={{ borderRadius: theme.spacing(1.5) }}>
            <CloseIcon />
          </IconButton>
        </Box>

        <DetailSection sx={{ p: 2.5, mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box
              onClick={handleOpenParticipantProfile}
              role={drawerParticipant.user?.id ? 'button' : undefined}
              tabIndex={drawerParticipant.user?.id ? 0 : undefined}
              onKeyDown={(e) => {
                if (drawerParticipant.user?.id && (e.key === 'Enter' || e.key === ' ')) {
                  e.preventDefault();
                  handleOpenParticipantProfile();
                }
              }}
              sx={{
                display: 'inline-flex',
                flexShrink: 0,
                borderRadius: '50%',
                cursor: drawerParticipant.user?.id ? 'pointer' : 'default',
                transition: 'transform 0.18s ease, box-shadow 0.18s ease',
                '&:hover': drawerParticipant.user?.id ? {
                  transform: 'scale(1.04)',
                  boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.14)}`
                } : {},
                '&:focus-visible': drawerParticipant.user?.id ? {
                  outline: `2px solid ${theme.palette.primary.main}`,
                  outlineOffset: 3
                } : {}
              }}
            >
              <Avatar
                src={drawerParticipant.user?.avatar}
                sx={{
                  width: 70,
                  height: 70,
                  boxShadow: theme.shadows[1],
                  bgcolor: drawerParticipant.participation_type === 'speaker'
                    ? 'primary.main'
                    : 'grey.400'
                }}
              >
                {getInitials(drawerParticipant.user?.first_name, drawerParticipant.user?.last_name)}
              </Avatar>
            </Box>

            <Box sx={{ flex: 1 }}>
              <Typography
                variant="h6"
                fontWeight="700"
                onClick={handleOpenParticipantProfile}
                sx={{
                  color: drawerParticipant.user?.id ? 'primary.main' : 'text.primary',
                  cursor: drawerParticipant.user?.id ? 'pointer' : 'default',
                  width: 'fit-content',
                  transition: 'color 0.18s ease',
                  '&:hover': drawerParticipant.user?.id
                    ? { color: 'primary.dark' }
                    : {}
                }}
              >
                {drawerParticipant.user?.first_name} {drawerParticipant.user?.last_name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {drawerParticipant.user?.email}
              </Typography>
              {drawerParticipant.user?.branch && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                  <LocationOnIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                  <Typography variant="caption" color="text.disabled">
                    {drawerParticipant.user.branch}
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: 2, mt: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <Chip
              label={drawerParticipant.participation_type === 'speaker' ? 'Докладчик' : 'Слушатель'}
              icon={drawerParticipant.participation_type === 'speaker' ? '🎤' : '👂'}
              size="small"
              sx={{
                borderRadius: theme.spacing(1.5),
                bgcolor: drawerParticipant.participation_type === 'speaker'
                  ? alpha(theme.palette.primary.main, 0.1)
                  : alpha(theme.palette.secondary.main, 0.1),
                color: drawerParticipant.participation_type === 'speaker'
                  ? 'primary.main'
                  : 'secondary.main',
                fontWeight: 600,
                height: 28,
                '& .MuiChip-label': {
                  px: 2
                }
              }}
            />

            <StatusChip
              label={drawerParticipant.status === 'approved' ? 'Подтверждён' :
                     drawerParticipant.status === 'pending' ? 'Ожидает' : 'Отклонён'}
              statuscolor={drawerParticipant.status}
              icon={
                drawerParticipant.status === 'approved' ? <VerifiedIcon /> :
                drawerParticipant.status === 'pending' ? <HowToRegIcon /> :
                <CancelIcon />
              }
              sx={{ height: 28 }}
            />

            {drawerParticipant.section && (
              <Chip
                label={drawerParticipant.section.title}
                size="small"
                sx={{
                  borderRadius: theme.spacing(5),
                  height: 28,
                  backgroundColor: sectionBgColor,
                  color: sectionColor,
                  fontWeight: 600,
                  fontSize: '0.75rem',
                  maxWidth: 200,
                  ml: '1',
                  '& .MuiChip-label': {
                    px: 1.5,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }
                }}
              />
            )}
          </Box>
        </DetailSection>

        <DetailSection sx={{ p: 2, mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
              Дата подачи заявки
            </Typography>
            <Typography variant="body2" fontWeight="600">
              {drawerParticipant.created_at
                ? new Date(drawerParticipant.created_at).toLocaleDateString('ru-RU', {
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })
                : 'Дата не указана'}
            </Typography>
          </Box>
        </DetailSection>

        {drawerParticipant.project && (
          <DetailSection sx={{ p: 2, mb: 2 }}>
            <SectionTitle sx={{ fontSize: '0.7rem', mb: 0.5 }}>Связанный проект</SectionTitle>
            <Typography variant="body2" fontWeight="500">
              {drawerParticipant.project.title}
            </Typography>
          </DetailSection>
        )}

        {drawerParticipant.uploaded_files &&
         Array.isArray(drawerParticipant.uploaded_files) &&
         drawerParticipant.uploaded_files.length > 0 && (
          <DetailSection sx={{ p: 2, mb: 2 }}>
            <SectionTitle sx={{ fontSize: '0.7rem', mb: 1 }}>Загруженные файлы</SectionTitle>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {drawerParticipant.uploaded_files
                .filter(file => file !== null && file !== undefined)
                .map((file, index) => {
                  const fileName = file?.name || file?.filename || file?.title || 'Файл';
                  const fileUrl = file?.url || file?.path || '#';

                  return (
                    <Button
                      key={index}
                      variant="outlined"
                      size="small"
                      startIcon={<DescriptionIcon />}
                      onClick={() => {
                        if (fileUrl && fileUrl !== '#') {
                          window.open(fileUrl, '_blank');
                        }
                      }}
                      disabled={!fileUrl || fileUrl === '#'}
                      sx={{
                        justifyContent: 'flex-start',
                        textTransform: 'none',
                        borderRadius: theme.spacing(1.5),
                        borderColor: alpha(theme.palette.primary.main, 0.3),
                        color: 'text.primary',
                        fontSize: '0.75rem',
                        py: 0.5,
                        '&:hover': {
                          borderColor: 'primary.main',
                          backgroundColor: alpha(theme.palette.primary.main, 0.02)
                        },
                        '&.Mui-disabled': {
                          opacity: 0.5,
                          cursor: 'not-allowed'
                        }
                      }}
                    >
                      {fileName}
                    </Button>
                  );
              })}
            </Box>
          </DetailSection>
        )}

        {drawerParticipant.status === 'pending' && canManageApplications && (
          <Box sx={{
            display: 'flex',
            gap: 2,
            mt: 'auto',
            pt: 2,
            borderTop: '1px solid',
            borderColor: 'divider'
          }}>
            <Button
              fullWidth
              variant="contained"
              color="success"
              onClick={(e) => {
                handleApprove(drawerParticipant, e);
              }}
              disabled={!!actionLoading[drawerParticipant.id]}
              sx={{
                borderRadius: theme.spacing(5),
                py: 1.5
              }}
            >
              {actionLoading[drawerParticipant.id] === 'approve' ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                'Одобрить'
              )}
            </Button>
            <Button
              fullWidth
              variant="contained"
              color="error"
              onClick={(e) => {
                handleReject(drawerParticipant, e);
              }}
              disabled={!!actionLoading[drawerParticipant.id]}
              sx={{
                borderRadius: theme.spacing(5),
                py: 1.5
              }}
            >
              {actionLoading[drawerParticipant.id] === 'reject' ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                'Отклонить'
              )}
            </Button>
          </Box>
        )}
      </Box>
    );
  };

  return (
    <>
      <StyledDialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        disableEnforceFocus
        style={{ zIndex: 1300 }}
      >
        <StyledDialogTitle>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h5" fontWeight="700" sx={{ color: 'primary.main', lineHeight: 1.15 }}>
              Участники мероприятия
            </Typography>
            <Box sx={{ display: 'flex', gap: 0.8, mt: 1, flexWrap: 'wrap' }}>
              <SummaryPill>
                <PersonIcon sx={{ fontSize: 15, color: 'primary.main' }} />
                {approvedParticipants.length} подтверждено
              </SummaryPill>
              {canManageApplications && pendingRequests.length > 0 && (
                <SummaryPill>
                  <HowToRegIcon sx={{ fontSize: 15, color: 'warning.main' }} />
                  {pendingRequests.length} заявок
                </SummaryPill>
              )}
              {rejectedParticipants.length > 0 && (
                <SummaryPill>
                  <CancelIcon sx={{ fontSize: 15, color: 'text.disabled' }} />
                  {rejectedParticipants.length} отклонено
                </SummaryPill>
              )}
            </Box>
          </Box>
          <IconButton
            onClick={onClose}
            sx={{
              width: 40,
              height: 40,
              flexShrink: 0,
              color: 'text.secondary',
              backgroundColor: alpha(theme.palette.common.white, 0.44),
              border: `1px solid ${alpha(theme.palette.common.white, 0.80)}`,
              '&:hover': {
                color: 'primary.main',
                backgroundColor: alpha(theme.palette.primary.main, 0.09)
              }
            }}
          >
            <CloseIcon />
          </IconButton>
        </StyledDialogTitle>

        <DialogContent
          aria-busy={isRefreshing}
          sx={{
            p: 2.2,
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
            overflow: 'hidden'
          }}
        >
          <SearchField
            fullWidth
            placeholder="🔍 Поиск по имени, email, филиалу или секции..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{ mb: 1.6 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: 'text.secondary' }} />
                </InputAdornment>
              ),
              endAdornment: searchQuery && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearchQuery('')}>
                    <CloseIconSmall fontSize="small" />
                  </IconButton>
                </InputAdornment>
              )
            }}
          />

          <Box sx={{ mb: 1.6 }}>
            <StyledTabs variant="fullWidth" value={tabValue} onChange={handleTabChange}>
              <Tab
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PersonIcon fontSize="small" />
                    <span>Участники</span>
                    <Badge
                      badgeContent={approvedParticipants.length}
                      color="primary"
                      sx={{
                        '& .MuiBadge-badge': {
                          position: 'relative',
                          transform: 'none',
                          ml: 0.6,
                          fontWeight: 700,
                          fontSize: '0.65rem',
                          minWidth: 19,
                          height: 19,
                          borderRadius: 999
                        }
                      }}
                    />
                  </Box>
                }
              />
              {canManageApplications && (
                <Tab
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <HowToRegIcon fontSize="small" />
                      <span>Заявки</span>
                      <Badge
                        badgeContent={pendingRequests.length}
                        color="warning"
                        sx={{
                          '& .MuiBadge-badge': {
                            position: 'relative',
                            transform: 'none',
                            ml: 1,
                            fontWeight: 600
                          }
                        }}
                      />
                    </Box>
                  }
                />
              )}
              {canManageApplications && (
                <Tab
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CancelIcon fontSize="small" />
                      <span>Отклонённые</span>
                      <Badge
                        badgeContent={rejectedParticipants.length}
                        color="error"
                        sx={{
                          '& .MuiBadge-badge': {
                            position: 'relative',
                            transform: 'none',
                            ml: 1,
                            fontWeight: 600
                          }
                        }}
                      />
                    </Box>
                  }
                />
              )}
            </StyledTabs>
          </Box>

          <ListContainer>
            {isRefreshing ? (
              <ParticipantsSkeleton theme={theme} />
            ) : (
              <>
            {tabValue === 0 && (
              filteredApproved.length > 0 ? (
                <List>
                  {filteredApproved.map((p) => (
                    <AnimatedParticipantItem
                      key={p.id}
                      participant={p}
                      showActions={false}
                      onApprove={handleApprove}
                      onReject={handleReject}
                      onItemClick={handleRowClick}
                    />
                  ))}
                </List>
              ) : (
                <Box sx={{
                  textAlign: 'center',
                  py: 6,
                  px: 2,
                  backgroundColor: 'grey.50',
                  borderRadius: theme.spacing(1.5)
                }}>
                  <PersonIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2, opacity: 0.5 }} />
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    {searchQuery ? 'Ничего не найдено' : 'Нет подтверждённых участников'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {searchQuery ? 'Попробуйте изменить параметры поиска' : 'Когда кто-то подаст заявку, она появится здесь'}
                  </Typography>
                </Box>
              )
            )}

            {canManageApplications && tabValue === 1 && (
              filteredPending.length > 0 ? (
                <List>
                  {filteredPending.map((p) => (
                    <AnimatedParticipantItem
                      key={p.id}
                      participant={p}
                      showActions={true}
                      onApprove={handleApprove}
                      onReject={handleReject}
                      onItemClick={handleRowClick}
                    />
                  ))}
                </List>
              ) : (
                <Box sx={{
                  textAlign: 'center',
                  py: 6,
                  px: 2,
                  backgroundColor: 'grey.50',
                  borderRadius: theme.spacing(1.5)
                }}>
                  <HowToRegIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2, opacity: 0.5 }} />
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    {searchQuery ? 'Ничего не найдено' : 'Нет новых заявок'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {searchQuery ? 'Попробуйте изменить параметры поиска' : 'Когда кто-то подаст заявку, она появится здесь'}
                  </Typography>
                </Box>
              )
            )}

            {canManageApplications && tabValue === 2 && (
              filteredRejected.length > 0 ? (
                <List>
                  {filteredRejected.map((p) => (
                    <AnimatedParticipantItem
                      key={p.id}
                      participant={p}
                      showActions={false}
                      onApprove={handleApprove}
                      onReject={handleReject}
                      onItemClick={handleRowClick}
                    />
                  ))}
                </List>
              ) : (
                <Box sx={{
                  textAlign: 'center',
                  py: 6,
                  px: 2,
                  backgroundColor: 'grey.50',
                  borderRadius: theme.spacing(1.5)
                }}>
                  <CancelIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2, opacity: 0.5 }} />
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    {searchQuery ? 'Ничего не найдено' : 'Нет отклонённых заявок'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {searchQuery ? 'Попробуйте изменить параметры поиска' : 'Отклонённые заявки будут отображаться здесь'}
                  </Typography>
                </Box>
              )
            )}
              </>
            )}
          </ListContainer>
        </DialogContent>
      </StyledDialog>

      <StyledDrawer
        anchor="right"
        open={detailDrawerOpen}
        onClose={handleCloseDrawer}
        SlideProps={{ direction: 'left' }}
        transitionDuration={300}
        hideBackdrop={false}
        style={{ zIndex: 1300 }}
        ModalProps={{
          disableEnforceFocus: true,
          hideBackdrop: false,
          style: { zIndex: 1300 }
        }}
      >
        {renderParticipantDetails()}
      </StyledDrawer>
    </>
  );
};

export default EventParticipantsModal;